import _ from 'underscore';

import { Messages } from '../../codegen/Localize';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { IOAuthConfigInfo, IOAuthCredential, IOAuthCredentialTypeInfo } from '../serverApi/types/OAuth/IOAuthCredential';

// Use these strings to display info about a particular OAuth provider
export interface IOAuthCredentialDisplayStrings {
  displayName: string;
  promptEmbedCredential: string;
  noCredential: string;
  describeEmbedCredential: string;
  describeNoCredential: string;
}

// Base interface for getting information about what OAuth providers are available on the server
// as well as the behavior of the individual providers


// Implementation of IOAuthCredentialsService which gets its information from the sessionManagementService
export namespace OAuthCredentialsService  {

  export function getTypeInfo(typeStringKey: string): IOAuthCredentialTypeInfo {
    return _.find(this.getAllOAuthCredentialTypes(), info => info.type === typeStringKey
      || info.type === getStorageProviderFromCloudFileClassName(typeStringKey));
  }

  export function getAllOAuthCredentialTypes(): Array<IOAuthCredentialTypeInfo> {
    return getSessionManagementServiceInstance().oauthSettings() || [];
  }

  export function canAddForConnectionType(typeStringKey: string): boolean {
    return true; // anything we get back from the server is enabled
  }

  export function supportsGenericAuth(typeStringKey: string): boolean {
    const info = this.getTypeInfo(typeStringKey);
    return info ? info.supportsGenericAuth : false;
  }

  export function shouldIgnoreDefaultOAuthConfigId(oauthConfigInfo: IOAuthConfigInfo): boolean {
    return oauthConfigInfo.oauthConfigId === 'default' &&  oauthConfigInfo.configLabel === 'null-config-label';
  }

  export function supportsTestConnection(typeStringKey: string, credential?: IOAuthCredential): boolean {
    const info = this.getTypeInfo(typeStringKey);
    return info ? info.supportsTestToken : false;
  }

  export function getDisplayStrings(typeStringKey: string): IOAuthCredentialDisplayStrings {
    // Just return the generic description for everything except the provider's name
    const info = this.getTypeInfo(typeStringKey);
    return info && {
        displayName: getDisplayNameFromInfo(info),
        promptEmbedCredential : Messages.editDataConnections_generic_embedCredential(),
        noCredential: Messages.editDataConnections_generic_noCredential(),
        describeEmbedCredential: Messages.editDataConnections_generic_describeEmbedCredential(),
        describeNoCredential: Messages.editDataConnections_generic_describeNoCredential()
    };
  }

  export function getDisplayNameFromInfo(info: IOAuthCredentialTypeInfo): string {
     if (!info || !info.displayNames) {
       return '';
     }

     // info.displayNames will contain a mapping of language to provider localized name.
     // For a few special cases (region specific language codes like en_GB) the key will use locale.
     let lang = getSessionManagementServiceInstance().sessionLanguage();
     if (!info.displayNames.hasOwnProperty(lang)) {
       // Just set it to english if we don't have the language present
       lang = 'en';
     }

     // Coalesce to '' so a type with an empty/partial displayNames map never yields an undefined label.
     // A label-less item crashes @tableau/widgets SingleSelectDropdown (which unmounts the whole dialog);
     // see W-23915429 where llm-gateway was surfaced with an empty displayNames map.
     return info.displayNames[lang] || '';
  }

  export function getInfoFromDisplayName(displayName: string): IOAuthCredentialTypeInfo {
    let matchingTypeInfo;
    let allCredentialTypes = OAuthCredentialsService.getAllOAuthCredentialTypes();
    const matchingCredentialType = allCredentialTypes.filter(
      typeInfo => OAuthCredentialsService.getDisplayNameFromInfo(typeInfo) === displayName);
    if (matchingCredentialType.length > 0) {
      // there should only be one match, if there are more, we just match to the first one.
      matchingTypeInfo = matchingCredentialType[0];
    }
    return matchingTypeInfo;
  }

  // For flow keychain, strip provider name from class name if it's a cloud file connection.
  // This is a copy of the native code logic:
  // teams_near/modules/connectors/tabconnect/main/infrastructure/CloudFileConnectorsRegistry.cpp
  function getStorageProviderFromCloudFileClassName(dbClass: string): string {
    if (dbClass.startsWith('cloudfile')) {
      // A full cloudfile class name is like this: `cloudfile:dropbox-textscan`
      const provider = dbClass.split(':')[1].split('-')[0];
      return provider;
    }
    return dbClass;
  }

  export function getDisplayNameFromType(connectionType: string): string {
    return getDisplayNameFromInfo(OAuthCredentialsService.getTypeInfo(connectionType));
  }
}

export type OAuthCredentialsService = typeof OAuthCredentialsService;
